import SignalWifi4BarIcon from '@mui/icons-material/SignalWifi4Bar';
import SignalWifiOffIcon from '@mui/icons-material/SignalWifiOff';
import SyncIcon from '@mui/icons-material/Sync';
import SyncDisabledIcon from '@mui/icons-material/SyncDisabled';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import * as React from 'react';

export default function RealtimeStatus({ lastUpdate = null, connectionStatus = 'connecting', isRealTimeEnabled = true }) {
  const theme = useTheme();
  const [currentTime, setCurrentTime] = React.useState(new Date());

  // Atualiza o tempo atual a cada 1 segundo se houver lastUpdate
  React.useEffect(() => {
    let interval;

    if (lastUpdate) {
      // Atualiza imediatamente quando lastUpdate muda
      setCurrentTime(new Date());

      interval = setInterval(() => {
        setCurrentTime(new Date());
      }, 1000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [lastUpdate]);

  const getStatusInfo = () => {
    if (!isRealTimeEnabled) {
      return {
        icon: <SyncDisabledIcon />,
        color: 'default',
        label: 'Desabilitado',
        description: 'Tempo real disponível apenas para hoje'
      };
    }

    switch (connectionStatus) {
      case 'connected':
        return {
          icon: <SignalWifi4BarIcon />,
          color: 'success',
          label: 'Conectado',
          description: 'Atualizações em tempo real ativas'
        };
      case 'disconnected':
        return {
          icon: <SignalWifiOffIcon />,
          color: 'error',
          label: 'Desconectado',
          description: 'Tentando reconectar...'
        };
      case 'connecting':
        return {
          icon: <SyncIcon />,
          color: 'warning',
          label: 'Conectando',
          description: 'Estabelecendo conexão...'
        };
      case 'rejected':
        return {
          icon: <SignalWifiOffIcon />,
          color: 'error',
          label: 'Rejeitado',
          description: 'Falha na autenticação'
        };
      default:
        return {
          icon: <SyncIcon />,
          color: 'default',
          label: 'Desconhecido',
          description: 'Status não identificado'
        };
    }
  };

  const formatLastUpdate = (date) => {
    if (!date) return '--:--';

    return new Intl.DateTimeFormat('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    }).format(date);
  };

  const getTimeSinceLastUpdate = (date, now) => {
    if (!date) return null;

    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 0) {
      return '0s';
    }

    if (diffInSeconds < 60) {
      return `${diffInSeconds}s`;
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes}m`;
    } else {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours}h`;
    }
  };

  const statusInfo = getStatusInfo();

  return (
    <Card
      variant="outlined"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        flexGrow: 1,
        borderColor: connectionStatus === 'connected' && isRealTimeEnabled ? 'success.main' :
          connectionStatus === 'disconnected' || connectionStatus === 'rejected' ? 'error.main' :
            'divider'
      }}
    >
      <CardContent sx={{ pb: 2 }}>
        <Stack spacing={2}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{
              color: statusInfo.color === 'success' ? 'success.main' :
                statusInfo.color === 'error' ? 'error.main' :
                  statusInfo.color === 'warning' ? 'warning.main' :
                    'text.secondary'
            }}>
              {statusInfo.icon}
            </Box>
            <Typography component="h2" variant="subtitle2">
              Tempo Real
            </Typography>
          </Box>

          {/* Status */}
          <Box>
            <Chip
              label={statusInfo.label}
              color={statusInfo.color}
              size="small"
              sx={{ mb: 1 }}
              variant="outlined"
            />
            <Typography variant="caption" color="text.secondary" display="block">
              {statusInfo.description}
            </Typography>
          </Box>

          {/* Last Update */}
          {isRealTimeEnabled && (
            <Box>
              <Typography variant="body2" color="text.primary">
                <strong>{formatLastUpdate(lastUpdate)}</strong>
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {lastUpdate ? `Última atualização ${getTimeSinceLastUpdate(lastUpdate, currentTime)} atrás` : 'Aguardando primeira atualização...'}
              </Typography>
            </Box>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
} 